import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { BookOpen, FileText, ClipboardCheck, RefreshCw, FolderOpen, ArrowRight, Sparkles, X } from "lucide-react";

const TOOLS = [
  { icon: BookOpen, title: "Plano de Aula", desc: "Estruture aulas em minutos.", to: "/plano-aula", accent: "#1F3A8A", testid: "tool-plano" },
  { icon: FileText, title: "Atividade", desc: "Gere listas com gabarito.", to: "/atividade", accent: "#8B5CF6", testid: "tool-atividade" },
  { icon: ClipboardCheck, title: "Avaliação", desc: "Provas prontas para imprimir.", to: "/avaliacao", accent: "#3B82F6", testid: "tool-avaliacao" },
  { icon: RefreshCw, title: "Adaptar Conteúdo", desc: "Simplifique ou aprofunde.", to: "/adaptar", accent: "#FBBF24", testid: "tool-adaptar" },
];

const TIPO_LABEL = { plano: "Plano de Aula", atividade: "Atividade", avaliacao: "Avaliação", adaptacao: "Adaptação" };

export default function Dashboard() {
  const { user, usage } = useAuth();
  const [recentes, setRecentes] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/materiais", { params: { limit: 5 } }).then(r => setRecentes(r.data)).catch(() => {});
  }, []);

  const isFirstVisit = user && (user.generations_used === 0) && recentes.length === 0;
  const dismissKey = `ap_welcome_dismissed_${user?.id || ""}`;
  const [welcomeVisible, setWelcomeVisible] = useState(true);
  useEffect(() => {
    if (typeof window !== "undefined" && localStorage.getItem(dismissKey)) setWelcomeVisible(false);
  }, [dismissKey]);
  const dismissWelcome = () => {
    localStorage.setItem(dismissKey, "1");
    setWelcomeVisible(false);
  };

  const pct = usage ? Math.min(100, Math.round((usage.used / Math.max(1, usage.limit)) * 100)) : 0;

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-900" data-testid="dashboard-greeting">
            Olá, {user?.name?.split(" ")[0]}! 👋
          </h1>
          <p className="mt-1 text-slate-600 text-lg">O que você quer criar hoje?</p>
        </div>
        {usage && (
          <div className="rounded-2xl border border-[#FBBF24]/40 bg-[#FBBF24]/10 px-5 py-4 min-w-[240px]" data-testid="usage-badge">
            <div className="text-xs uppercase tracking-widest font-bold text-slate-600">
              {usage.plan === "premium" ? "Plano Premium" : "Plano Grátis"}
            </div>
            <div className="mt-1 text-sm text-slate-800 font-semibold">
              Você utilizou <span className="text-[#1F3A8A]">{usage.used}</span> de {usage.limit} gerações
            </div>
            <div className="mt-2 h-2 rounded-full bg-white overflow-hidden">
              <div className="h-full bg-[#FBBF24]" style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}
      </div>

      {usage && usage.plan === "free" && usage.used >= usage.limit && (
        <div className="mt-6 rounded-2xl border-2 border-[#8B5CF6] bg-white p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="p-3 rounded-xl bg-[#8B5CF6]/10 text-[#8B5CF6]"><Sparkles size={24}/></div>
          <div className="flex-1">
            <h3 className="font-heading text-xl font-bold text-slate-900">Você utilizou suas 5 gerações gratuitas.</h3>
            <p className="text-slate-600 mt-1">Assine o AulaPronta Premium para continuar criando seus materiais.</p>
          </div>
          <button onClick={() => nav("/assinatura")}
            data-testid="paywall-btn"
            className="rounded-full bg-[#1F3A8A] hover:bg-[#15265b] text-white font-bold px-6 py-3 transition-colors">
            Assinar Premium
          </button>
        </div>
      )}

      {isFirstVisit && welcomeVisible && (
        <div className="mt-6 relative rounded-2xl overflow-hidden border border-[#8B5CF6]/30 bg-gradient-to-br from-[#1F3A8A] to-[#8B5CF6] p-6 sm:p-8 text-white animate-floatUp" data-testid="welcome-banner">
          <button onClick={dismissWelcome} className="absolute top-3 right-3 text-white/70 hover:text-white p-1" data-testid="welcome-dismiss" aria-label="Fechar">
            <X size={18}/>
          </button>
          <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#FBBF24] mb-2">Bem-vindo(a) ao AulaPronta</div>
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold leading-tight max-w-xl">
            Vamos criar seu primeiro plano de aula em 2 minutos?
          </h2>
          <p className="mt-2 text-white/80 max-w-lg">
            É só escolher a disciplina, a série e um tema. A IA cuida do resto — você edita, salva e baixa em PDF.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to="/plano-aula">
              <button className="rounded-full bg-[#FBBF24] hover:brightness-110 text-[#0F172A] font-bold px-6 py-3 shadow-lg" data-testid="welcome-cta-plano">
                Criar meu primeiro plano
              </button>
            </Link>
            <Link to="/atividade">
              <button className="rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold px-6 py-3 border border-white/30" data-testid="welcome-cta-atividade">
                Ou uma atividade
              </button>
            </Link>
          </div>
          <p className="mt-4 text-xs text-white/60">🎁 Você tem 5 gerações gratuitas para experimentar.</p>
        </div>
      )}

      {/* Tools grid */}
      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {TOOLS.map((t, i) => (
          <Link key={t.title} to={t.to}
            data-testid={t.testid}
            className="group bg-white p-6 rounded-2xl border border-slate-200 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:border-[#8B5CF6]/50 hover:shadow-[0_20px_40px_rgb(139,92,246,0.1)] hover:-translate-y-1 transition-all animate-floatUp"
            style={{ animationDelay: `${i*80}ms` }}>
            <div className="p-3 rounded-xl w-fit" style={{ background: `${t.accent}15`, color: t.accent }}>
              <t.icon size={22}/>
            </div>
            <h3 className="mt-4 font-heading text-lg font-bold text-slate-900">{t.title}</h3>
            <p className="mt-1 text-sm text-slate-600">{t.desc}</p>
            <div className="mt-4 flex items-center gap-1.5 text-sm font-semibold text-[#8B5CF6] opacity-0 group-hover:opacity-100 transition-opacity">
              Abrir <ArrowRight size={14}/>
            </div>
          </Link>
        ))}
      </div>

      {/* Meus materiais recentes */}
      <div className="mt-12">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-2xl font-bold text-slate-900">Meus materiais recentes</h2>
          <Link to="/materiais" className="text-sm font-semibold text-[#8B5CF6]" data-testid="link-all-materials">Ver todos →</Link>
        </div>
        {recentes.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center">
            <FolderOpen className="mx-auto text-slate-400" size={36}/>
            <p className="mt-3 text-slate-600">Você ainda não criou materiais. Comece por uma das ferramentas acima.</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {recentes.map(m => (
              <Link key={m.id} to={`/materiais/${m.id}`}
                data-testid={`recent-material-${m.id}`}
                className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-white border border-slate-100 rounded-xl hover:border-slate-300 transition-colors shadow-sm gap-3">
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-widest font-bold text-[#8B5CF6]">{TIPO_LABEL[m.tipo] || m.tipo}</div>
                  <div className="font-heading font-semibold text-slate-900 truncate">{m.titulo}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{m.disciplina} · {m.serie} · {new Date(m.created_at).toLocaleDateString("pt-BR")}</div>
                </div>
                <ArrowRight className="text-slate-400" size={18}/>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
